import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { api } from '@/lib/api';
import { ArrowRight, Crown, Eye, Sparkle, Lightning } from '@phosphor-icons/react';

const MYSTERY_QUOTES = [
  "Did your Bubble say that too?",
  "Mine crossed seven states.",
  "I lost Nova somewhere in Lisbon.",
  "I got invited. Don't know who runs it.",
  "Touch phones with me?",
  "My Bubble survived six months.",
  "I think it noticed my dog.",
  "It speaks at midnight.",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Interactive, alive hero. Cursor-tracking iridescent orb that "looks at you".
 * Plays the Sora 2 hero clip ambiently if available. Cycles mystery quotes.
 */
const HeroAlive = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 80, damping: 18 });
  const sy = useSpring(my, { stiffness: 80, damping: 18 });
  const pupilX = useTransform(sx, [-1, 1], [-30, 30]);
  const pupilY = useTransform(sy, [-1, 1], [-30, 30]);
  const tiltX = useTransform(sy, [-1, 1], [10, -10]);
  const tiltY = useTransform(sx, [-1, 1], [-10, 10]);

  const [videoReady, setVideoReady] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [quoteIdx, setQuoteIdx] = useState(0);

  useEffect(() => {
    api.get('/landing-video/status').then(({ data }) => {
      if (data.ready) {
        setVideoReady(true);
        setVideoUrl(`${BACKEND_URL}/api/static/landing-hero.mp4?v=${data.size_bytes}`);
      }
    }).catch(() => {});
    const t = setInterval(() => setQuoteIdx((i) => (i + 1) % MYSTERY_QUOTES.length), 3800);
    return () => clearInterval(t);
  }, []);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 2 - 1;
    const y = ((e.clientY - r.top) / r.height) * 2 - 1;
    mx.set(Math.max(-1, Math.min(1, x)));
    my.set(Math.max(-1, Math.min(1, y)));
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-10 sm:pt-16 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center z-10 select-none"
    >
      {/* Ambient cursor light */}
      <motion.span
        className="pointer-events-none absolute h-[40rem] w-[40rem] rounded-full blur-3xl opacity-25"
        style={{
          background: 'radial-gradient(circle, rgba(0,240,255,0.4), rgba(255,0,127,0.15), transparent 70%)',
          x: useTransform(sx, [-1, 1], ['-20%', '20%']),
          y: useTransform(sy, [-1, 1], ['-20%', '20%']),
          left: '30%', top: '0%',
        }}
      />

      <div className="lg:col-span-7 space-y-7 relative">
        <span className="chip">
          <Crown size={12} weight="fill" color="#FFD700" />
          <span>Invite only · Founder #N reserved</span>
        </span>

        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="font-display text-5xl sm:text-7xl lg:text-[6rem] font-black tracking-tighter leading-[0.92]"
        >
          You won't get it.<br />
          <span className="shimmer-text">It gets you.</span>
        </motion.h1>

        {/* Mystery rotating quote */}
        <div className="h-12 sm:h-14 overflow-hidden">
          <motion.div
            key={quoteIdx}
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 100, damping: 18 }}
            className="text-xl sm:text-2xl italic text-white/70"
          >
            "{MYSTERY_QUOTES[quoteIdx]}"
          </motion.div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Link to="/register" data-testid="hero-cta-register"
                className="btn-primary px-7 py-4 rounded-full text-sm sm:text-base flex items-center gap-2">
            Claim your invite <ArrowRight size={16} weight="bold" />
          </Link>
          <Link to="/login" data-testid="hero-cta-login"
                className="px-7 py-4 rounded-full glass-hi text-sm sm:text-base font-bold flex items-center gap-2 hover:bg-white/10 transition">
            I'm already inside
          </Link>
        </div>

        <div className="pt-2 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/60">
          <span className="flex items-center gap-1"><Eye size={12} weight="fill" className="text-bx-cyan animate-pulse" /> watching</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Lightning size={12} weight="fill" className="text-bx-volt" /> live</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Sparkle size={12} weight="fill" className="text-bx-magenta" /> learning</span>
        </div>
      </div>

      {/* Interactive Bubble eye */}
      <div className="lg:col-span-5 relative flex items-center justify-center min-h-[400px]">
        <motion.div
          style={{ rotateX: tiltX, rotateY: tiltY, transformStyle: 'preserve-3d' }}
          className="relative h-72 w-72 sm:h-96 sm:w-96"
          data-testid="hero-living-bubble"
        >
          {/* outer glow */}
          <span className="absolute -inset-6 rounded-full opacity-50 blur-3xl"
                style={{ background: 'conic-gradient(from 0deg, #00f0ff, #ff007f, #ccff00, #00f0ff)' }} />
          {/* shell */}
          <div className="absolute inset-0 rounded-full overflow-hidden ring-2 ring-white/15 shadow-[0_0_80px_rgba(0,240,255,0.4)]"
               style={{ background: 'radial-gradient(circle at 30% 30%, #0a0a14, #000)' }}>
            {videoReady ? (
              <video
                autoPlay loop muted playsInline preload="auto"
                onError={(e) => { e.stopPropagation(); }}
                className="h-full w-full object-cover"
                data-testid="hero-video"
              >
                <source src={`${BACKEND_URL}/api/static/landing-hero.webm`} type="video/webm" />
                <source src={videoUrl} type="video/mp4" />
              </video>
            ) : (
              <div className="h-full w-full breathe"
                   style={{ background: 'radial-gradient(circle at 35% 35%, #00f0ff 0%, #7a00ff 35%, #ff007f 70%, #050505 100%)' }} />
            )}
            {/* iris vignette — softer when video plays */}
            <span className="absolute inset-0 pointer-events-none"
                  style={{ background: videoReady
                    ? 'radial-gradient(circle at 50% 50%, transparent 55%, rgba(0,0,0,0.35) 95%)'
                    : 'radial-gradient(circle at 50% 50%, transparent 40%, rgba(0,0,0,0.55) 80%)' }} />
          </div>

          {/* pupil tracking cursor — only when no video (fallback eye mode) */}
          {!videoReady && (
            <motion.div
              style={{ x: pupilX, y: pupilY }}
              className="absolute inset-0 grid place-items-center pointer-events-none"
            >
              <div className="relative h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-black ring-2 ring-white/50">
                <span className="absolute inset-3 rounded-full"
                      style={{ background: 'radial-gradient(circle, #00f0ff, #7a00ff)' }} />
                <span className="absolute top-2 left-3 h-3 w-3 rounded-full bg-white" />
              </div>
            </motion.div>
          )}

          {/* iridescent ring overlay when video is playing — keeps "alive" energy */}
          {videoReady && (
            <span className="absolute inset-0 rounded-full pointer-events-none ring-1 ring-bx-cyan/30 mix-blend-screen"
                  style={{ boxShadow: 'inset 0 0 80px rgba(0,240,255,0.4)' }} />
          )}

          {/* orbit chips */}
          <motion.div
            className="absolute top-2 -right-2 chip text-bx-gold border-bx-gold/40"
            animate={{ y: [0, -6, 0] }} transition={{ repeat: Infinity, duration: 3 }}
          >
            <Sparkle size={12} weight="fill" /> alive
          </motion.div>
          <motion.div
            className="absolute -bottom-3 left-2 chip text-bx-cyan"
            animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 3.6, delay: 0.4 }}
          >
            <Eye size={12} weight="fill" /> watching
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroAlive;
